export interface FrameTemplate {
  id: string;
  name: string;
  width: number;
  height: number;
}

export interface FrameTemplateCategory {
  id: string;
  name: string;
  templates: FrameTemplate[];
}

function template(id: string, name: string, width: number, height: number): FrameTemplate {
  return { id, name, width, height };
}

export const POPULAR_CATEGORY_ID = "popular";

/** Curated shortcuts — template ids must exist in the catalog below. */
export const POPULAR_TEMPLATE_IDS = [
  "macbook-air-13",
  "macbook-air-15",
  "macbook-pro-14",
  "iphone-16",
  "iphone-16-pro",
  "desktop-hd",
  "desktop-1440",
  "ipad-pro-11",
  "slide-16-9",
  "instagram-square",
  "open-graph",
  "wireframe-mobile",
] as const;

const FRAME_TEMPLATE_CATALOG: FrameTemplateCategory[] = [
  {
    id: "screens-desktop",
    name: "Screens — Desktop",
    templates: [
      template("desktop-hd", "HD 1080p", 1920, 1080),
      template("desktop-1440", "Desktop 1440", 1440, 1024),
      template("desktop-1280", "Desktop 1280", 1280, 800),
      template("desktop-wxga", "WXGA 1366", 1366, 768),
      template("desktop-qhd", "QHD 1440p", 2560, 1440),
      template("desktop-4k", "4K UHD", 3840, 2160),
      template("desktop-ultrawide", "Ultrawide 21:9", 3440, 1440),
    ],
  },
  {
    id: "screens-laptop",
    name: "Screens — Laptops",
    templates: [
      template("macbook-air-13", "MacBook Air 13\"", 1280, 832),
      template("macbook-air-15", "MacBook Air 15\"", 1470, 956),
      template("macbook-pro-14", "MacBook Pro 14\"", 1512, 982),
      template("macbook-pro-16", "MacBook Pro 16\"", 1728, 1117),
      template("imac-24", "iMac 24\"", 2240, 1260),
      template("studio-display", "Studio Display", 2560, 1440),
      template("pro-display-xdr", "Pro Display XDR", 3008, 1692),
      template("laptop-13", "Laptop 13\"", 1280, 800),
      template("laptop-15", "Laptop 15\"", 1440, 900),
    ],
  },
  {
    id: "screens-tablet",
    name: "Screens — Tablets",
    templates: [
      template("ipad-pro-13", "iPad Pro 13\"", 1376, 1032),
      template("ipad-pro-11", "iPad Pro 11\"", 1210, 834),
      template("ipad-air-11", "iPad Air 11\"", 1180, 820),
      template("ipad-air-13", "iPad Air 13\"", 1366, 1024),
      template("ipad-11", "iPad 11\"", 1180, 820),
      template("ipad-mini", "iPad mini", 1133, 744),
      template("tablet-android-10", "Android Tablet 10\"", 1280, 800),
      template("tablet-android-12", "Android Tablet 12\"", 1600, 2560),
    ],
  },
  {
    id: "screens-phone",
    name: "Screens — Phones",
    templates: [
      template("iphone-16-pro-max", "iPhone 16 Pro Max", 440, 956),
      template("iphone-16-pro", "iPhone 16 Pro", 402, 874),
      template("iphone-16-plus", "iPhone 16 Plus", 430, 932),
      template("iphone-16", "iPhone 16", 393, 852),
      template("iphone-se", "iPhone SE", 375, 667),
      template("iphone-15", "iPhone 15", 393, 852),
      template("android-compact", "Android Compact", 360, 800),
      template("android-medium", "Android Medium", 390, 844),
      template("android-large", "Android Large", 412, 915),
      template("pixel-9-pro", "Pixel 9 Pro", 412, 892),
      template("galaxy-s25", "Galaxy S25", 412, 915),
    ],
  },
  {
    id: "screens-watch",
    name: "Screens — Watch",
    templates: [
      template("apple-watch-45", "Apple Watch 45mm", 396, 484),
      template("apple-watch-41", "Apple Watch 41mm", 352, 430),
      template("apple-watch-ultra", "Apple Watch Ultra", 410, 502),
    ],
  },
  {
    id: "social",
    name: "Social Media",
    templates: [
      template("instagram-square", "Instagram Post", 1080, 1080),
      template("instagram-portrait", "Instagram Portrait", 1080, 1350),
      template("instagram-story", "Instagram Story", 1080, 1920),
      template("facebook-post", "Facebook Post", 1200, 630),
      template("facebook-cover", "Facebook Cover", 820, 312),
      template("x-post", "X Post", 1600, 900),
      template("x-header", "X Header", 1500, 500),
      template("linkedin-post", "LinkedIn Post", 1200, 1200),
      template("linkedin-cover", "LinkedIn Cover", 1584, 396),
      template("youtube-thumbnail", "YouTube Thumbnail", 1280, 720),
      template("youtube-banner", "YouTube Banner", 2560, 1440),
      template("tiktok-video", "TikTok Video", 1080, 1920),
      template("pinterest-pin", "Pinterest Pin", 1000, 1500),
      template("open-graph", "Open Graph", 1200, 630),
      template("discord-banner", "Discord Server Banner", 960, 540),
    ],
  },
  {
    id: "print",
    name: "Print & Documents",
    templates: [
      template("a4-portrait", "A4 Portrait", 595, 842),
      template("a4-landscape", "A4 Landscape", 842, 595),
      template("a3-portrait", "A3 Portrait", 842, 1191),
      template("a3-landscape", "A3 Landscape", 1191, 842),
      template("a5-portrait", "A5 Portrait", 420, 595),
      template("letter-portrait", "US Letter Portrait", 612, 792),
      template("letter-landscape", "US Letter Landscape", 792, 612),
      template("legal-portrait", "US Legal Portrait", 612, 1008),
      template("tabloid", "Tabloid", 792, 1224),
    ],
  },
  {
    id: "presentations",
    name: "Presentations",
    templates: [
      template("slide-16-9", "Slide 16:9", 1920, 1080),
      template("slide-16-9-hd", "Slide 16:9 HD", 1280, 720),
      template("slide-4-3", "Slide 4:3", 1024, 768),
      template("keynote-wide", "Keynote Wide", 1920, 1080),
      template("google-slides", "Google Slides", 1920, 1080),
    ],
  },
  {
    id: "product",
    name: "Product & Marketing",
    templates: [
      template("app-store-iphone-67", "App Store iPhone 6.7\"", 1290, 2796),
      template("app-store-iphone-65", "App Store iPhone 6.5\"", 1284, 2778),
      template("app-store-ipad", "App Store iPad 12.9\"", 2048, 2732),
      template("play-store-feature", "Play Store Feature", 1024, 500),
      template("app-icon-ios", "App Icon", 1024, 1024),
      template("favicon", "Favicon Sheet", 256, 256),
      template("dribbble-shot", "Dribbble Shot", 1600, 1200),
      template("email-header", "Email Header", 600, 200),
      template("banner-leaderboard", "Leaderboard Ad", 728, 90),
      template("banner-medium-rectangle", "Medium Rectangle", 300, 250),
    ],
  },
  {
    id: "layout",
    name: "Layout Grids",
    templates: [
      template("icon-sheet", "Icon Sheet", 560, 360),
      template("wireframe-mobile", "Mobile Wireframe", 390, 844),
      template("wireframe-tablet", "Tablet Wireframe", 834, 1194),
      template("wireframe-desktop", "Desktop Wireframe", 1440, 1024),
      template("component-artboard", "Component Artboard", 400, 400),
    ],
  },
];

function buildPopularCategory(catalog: readonly FrameTemplateCategory[]): FrameTemplateCategory {
  const templatesById = new Map<string, FrameTemplate>();
  for (const category of catalog) {
    for (const entry of category.templates) {
      templatesById.set(entry.id, entry);
    }
  }

  const templates = POPULAR_TEMPLATE_IDS.flatMap(id => {
    const entry = templatesById.get(id);
    return entry ? [entry] : [];
  });

  return {
    id: POPULAR_CATEGORY_ID,
    name: "Popular",
    templates,
  };
}

export const FRAME_TEMPLATE_CATEGORIES: FrameTemplateCategory[] = [
  buildPopularCategory(FRAME_TEMPLATE_CATALOG),
  ...FRAME_TEMPLATE_CATALOG,
];

export const FRAME_TEMPLATES: FrameTemplate[] = FRAME_TEMPLATE_CATALOG.flatMap(category => category.templates);
